/**
 * Catálogos — migración web de ares8075/ares8085/ares8095/con2075.
 *
 * Mantenimiento de los catálogos básicos: carteras, oficiales, marcas y
 * modelos, y sucursales. Cada pestaña lista su catálogo con alta y edición
 * por diálogo. Carteras, oficiales y marcas/modelos permiten eliminar (el
 * backend rechaza carteras/oficiales con préstamos asociados); las
 * sucursales solo se crean o modifican (la clave es el número de sucursal,
 * que no se puede cambiar al editar).
 *
 * Marcas y Modelos es además el catálogo de precios: precio de lista y
 * mínimo en RD$ que prellenan el alta de chasis y la carga masiva, y que
 * "Aplicar" empuja a las unidades disponibles del inventario (previa
 * confirmación con el conteo de unidades afectadas).
 *
 * `soloCatalogo` fija la vista a un solo catálogo y oculta las pestañas: lo
 * usa la pantalla Marcas y Modelos (modules/marcasmodelos) para embeber el
 * CRUD de marcamodelo sin duplicarlo.
 */
import React, { useState, useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { parseExcelPreview, downloadTemplateXlsx } from "modules/importacion/excelPreview";

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n) || n === 0) return "—";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Carga masiva del catálogo de precios: encabezados del Excel aceptados por
// campo, comparados sin acentos, mayúsculas ni espacios ("Precio Mínimo" →
// "preciominimo").
const encabezadoCanon = (h) =>
  String(h || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const CAMPOS_CARGA = {
  marca: ["marca"],
  modelo: ["modelo"],
  precio: ["precio", "preciolista", "precioventa", "preciodeventa"],
  mprecio: ["minimo", "mprecio", "preciominimo", "preciomin"],
};

const mapearEncabezados = (headers) => {
  const mapa = {}; // campo -> header original
  headers.forEach((h) => {
    const canon = encabezadoCanon(h);
    Object.keys(CAMPOS_CARGA).forEach((campo) => {
      if (!mapa[campo] && CAMPOS_CARGA[campo].includes(canon)) mapa[campo] = h;
    });
  });
  return mapa;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

// Dentro del hub de Configuración el marco lo pone el panel derecho: la
// sección va sin tarjeta propia, solo con separación entre bloques.
const sectionBoxHub = { mb: 3 };

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>Sin registros</Typography>
    </Stack>
  );
}

const CATALOGOS = [
  {
    clave: "cartera",
    etiqueta: "Carteras",
    singular: "cartera",
    eliminar: true,
    vacio: { descripcion: "", activo: "S" },
  },
  {
    clave: "oficial",
    etiqueta: "Oficiales",
    singular: "oficial",
    eliminar: true,
    vacio: { nombre: "", codigo: "", activo: "S" },
  },
  {
    clave: "marcamodelo",
    etiqueta: "Marcas y Modelos",
    singular: "marca/modelo",
    eliminar: true,
    vacio: { marca: "", modelo: "", precio: "", mprecio: "" },
  },
  {
    // Tipos de documento del vehículo (matrícula, placa, copias y lo que
    // agregue la normativa). Los consumen las pantallas de transferencia/
    // entrega/devolución de documentos vía /api/tiposdocumento_listar; la
    // descripción es el valor que se guarda en los blobs, por eso el backend
    // rechaza '*' y duplicados.
    clave: "tipodocumento",
    etiqueta: "Tipos de Documento",
    singular: "tipo de documento",
    eliminar: true,
    vacio: { descripcion: "", precio: "", activo: "S" },
  },
  {
    clave: "sucursal",
    etiqueta: "Sucursales",
    singular: "sucursal",
    eliminar: false,
    vacio: { sucursal: "", descripcion: "", direccion: "", telefono1: "" },
  },
];

export default function CatalogosConfig({ enHub, soloCatalogo } = {}) {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const catalogos = soloCatalogo ? CATALOGOS.filter((c) => c.clave === soloCatalogo) : CATALOGOS;
  const catalogo = catalogos[tab];

  const cargar = async (clave) => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/config_catalogo_listar", { catalogo: clave });
      if (ok) {
        setRows(
          (data.filas || []).map((r) => ({ ...r, id: clave === "sucursal" ? r.sucursal : r.id }))
        );
      } else {
        setRows([]);
        showAlert({ message: data.Error || "No se pudo listar el catálogo", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar(catalogo.clave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ---- Alta / edición ----
  const [editando, setEditando] = useState(null); // null=cerrado, 0=nuevo, id=editar
  const [form, setForm] = useState({});
  const [guardando, setGuardando] = useState(false);

  const setCampo = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const abrirNuevo = () => {
    setForm({ ...catalogo.vacio });
    setEditando(0);
  };

  const abrirEditar = (row) => {
    const f = {};
    Object.keys(catalogo.vacio).forEach((k) => {
      f[k] = row[k] === null || row[k] === undefined ? "" : String(row[k]);
      // Precios en cero = sin definir: el campo va vacío, no con "0".
      if ((k === "precio" || k === "mprecio") && !(parseFloat(f[k]) > 0)) f[k] = "";
    });
    setForm(f);
    setEditando(row.id);
  };

  const guardar = async () => {
    const requerido = Object.keys(catalogo.vacio)[0];
    if (!String(form[requerido] || "").trim()) {
      showAlert({ message: `El campo ${requerido} es obligatorio`, color: "warning" });
      return;
    }
    if (catalogo.clave === "sucursal" && !String(form.descripcion || "").trim()) {
      showAlert({ message: "La descripción es obligatoria", color: "warning" });
      return;
    }
    if (catalogo.clave === "marcamodelo") {
      const p = parseFloat(form.precio || "0") || 0;
      const m = parseFloat(form.mprecio || "0") || 0;
      if (p > 0 && m > p) {
        showAlert({ message: "El mínimo no puede ser mayor que el precio", color: "warning" });
        return;
      }
    }
    setGuardando(true);
    try {
      const esNuevo = editando === 0;
      const body = { catalogo: catalogo.clave };
      Object.keys(catalogo.vacio).forEach((k) => {
        body[k] = String(form[k] || "").trim();
      });
      if (catalogo.clave === "sucursal") {
        if (!esNuevo) body.modificar = true;
      } else if (!esNuevo) {
        body.id = editando;
      }
      const { ok, data } = await post("/api/config_catalogo_guardar", body);
      if (ok) {
        showAlert({ message: "Registro guardado", color: "success" });
        setEditando(null);
        cargar(catalogo.clave);
      } else {
        showAlert({ message: data.Error || "No se pudo guardar el registro", color: "error" });
      }
    } finally {
      setGuardando(false);
    }
  };

  // ---- Aplicar precios del catálogo al inventario (solo marcamodelo) ----
  const [aplicando, setAplicando] = useState(null); // fila del catálogo o null
  const [unidades, setUnidades] = useState(null); // conteo previo o null (cargando)
  const [aplicandoRun, setAplicandoRun] = useState(false);

  const abrirAplicar = async (row) => {
    setAplicando(row);
    setUnidades(null);
    const { ok, data } = await post("/api/config_catalogo_precios_aplicar", { id: row.id });
    if (ok) {
      setUnidades(data.unidades ?? 0);
    } else {
      setAplicando(null);
      showAlert({ message: data.Error || "No se pudo consultar el inventario", color: "error" });
    }
  };

  const aplicar = async () => {
    setAplicandoRun(true);
    try {
      const { ok, data } = await post("/api/config_catalogo_precios_aplicar", {
        id: aplicando.id,
        confirmar: true,
      });
      if (ok) {
        showAlert({ message: data.ok || "Precios aplicados", color: "success" });
        setAplicando(null);
      } else {
        showAlert({ message: data.Error || "No se pudo aplicar el catálogo", color: "error" });
      }
    } finally {
      setAplicandoRun(false);
    }
  };

  // ---- Carga masiva del catálogo de precios (Excel, solo marcamodelo) ----
  // Upsert por marca+modelo: si el modelo existe se actualizan sus precios
  // con el dato del Excel; si no, se inserta al catálogo.
  const inputExcelRef = useRef(null);
  const [carga, setCarga] = useState(null); // {fileName, filas, errores, nuevas, actualizadas}
  const [subiendo, setSubiendo] = useState(false);

  const seleccionarExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // permite re-escoger el mismo archivo
    if (!file) return;
    try {
      const { headers, rows: filasExcel, rowCount } = await parseExcelPreview(file, {
        maxPreviewRows: 5000,
      });
      if (rowCount > 5000) {
        showAlert({ message: "Máximo 5000 filas por carga", color: "warning" });
        return;
      }
      const mapa = mapearEncabezados(headers);
      if (!mapa.marca || !mapa.modelo) {
        showAlert({
          message: "El archivo debe tener columnas Marca y Modelo (y Precio / Mínimo)",
          color: "warning",
        });
        return;
      }
      const existentes = new Set(
        rows.map((r) => `${String(r.marca || "").trim().toUpperCase()}|${String(r.modelo || "")
          .trim()
          .toUpperCase()}`)
      );
      const filas = [];
      const errores = [];
      let nuevas = 0;
      let actualizadas = 0;
      filasExcel.forEach((f, i) => {
        const marca = String(f[mapa.marca] ?? "").trim();
        const modelo = String(f[mapa.modelo] ?? "").trim();
        if (!marca && !modelo) return; // fila vacía
        if (!marca || !modelo) {
          errores.push(`Fila ${i + 2}: marca y modelo son obligatorios`);
          return;
        }
        const precio = mapa.precio ? String(f[mapa.precio] ?? "").trim() : "";
        const mprecio = mapa.mprecio ? String(f[mapa.mprecio] ?? "").trim() : "";
        filas.push({ marca, modelo, precio, mprecio });
        if (existentes.has(`${marca.toUpperCase()}|${modelo.toUpperCase()}`)) actualizadas += 1;
        else nuevas += 1;
      });
      if (!filas.length) {
        showAlert({ message: "El archivo no tiene filas con marca y modelo", color: "warning" });
        return;
      }
      setCarga({ fileName: file.name, filas, errores, nuevas, actualizadas });
    } catch (err) {
      showAlert({ message: err.message || "No se pudo leer el archivo", color: "error" });
    }
  };

  const subirCarga = async () => {
    setSubiendo(true);
    try {
      const { ok, data } = await post("/api/config_catalogo_precios_carga", {
        filas: carga.filas,
      });
      if (ok) {
        const rechazadas = (data.errores || []).length;
        showAlert({
          message: rechazadas
            ? `${data.ok}; ${rechazadas} filas rechazadas`
            : data.ok || "Catálogo cargado",
          color: rechazadas ? "warning" : "success",
        });
        setCarga(null);
        cargar(catalogo.clave);
      } else {
        showAlert({ message: data.Error || "No se pudo cargar el catálogo", color: "error" });
      }
    } finally {
      setSubiendo(false);
    }
  };

  // ---- Eliminación ----
  const [eliminando, setEliminando] = useState(null); // fila a eliminar o null
  const [borrando, setBorrando] = useState(false);

  const eliminar = async () => {
    setBorrando(true);
    try {
      const { ok, data } = await post("/api/config_catalogo_eliminar", {
        catalogo: catalogo.clave,
        id: eliminando.id,
      });
      if (ok) {
        showAlert({ message: "Registro eliminado", color: "success" });
        setEliminando(null);
        cargar(catalogo.clave);
      } else {
        showAlert({ message: data.Error || "No se pudo eliminar el registro", color: "error" });
      }
    } finally {
      setBorrando(false);
    }
  };

  const colAcciones = {
    field: "acciones",
    headerName: "",
    width: catalogo.clave === "marcamodelo" ? 330 : catalogo.eliminar ? 210 : 110,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => abrirEditar(params.row)}
          sx={btnOutline(FP.color.navy)}
        >
          Editar
        </Button>
        {catalogo.clave === "marcamodelo" && (
          <Button
            size="small"
            startIcon={<SellOutlinedIcon />}
            disabled={
              !(parseFloat(params.row.precio) > 0) && !(parseFloat(params.row.mprecio) > 0)
            }
            onClick={() => abrirAplicar(params.row)}
            sx={btnOutline(FP.color.navy)}
          >
            Aplicar
          </Button>
        )}
        {catalogo.eliminar && (
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon />}
            onClick={() => setEliminando(params.row)}
            sx={btnOutline("#B3261E")}
          >
            Eliminar
          </Button>
        )}
      </Stack>
    ),
  };

  const columnasPorCatalogo = {
    cartera: [
      { field: "id", headerName: "ID", width: 70 },
      { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 220 },
      { field: "activo", headerName: "Activo", width: 90 },
    ],
    tipodocumento: [
      { field: "id", headerName: "ID", width: 70 },
      { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 220 },
      {
        field: "precio",
        headerName: "Tarifa",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => (parseFloat(value) > 0 ? fmtMoney(value) : "—"),
      },
      { field: "activo", headerName: "Activo", width: 90 },
    ],
    oficial: [
      { field: "id", headerName: "ID", width: 70 },
      { field: "nombre", headerName: "Nombre", flex: 1, minWidth: 220 },
      { field: "codigo", headerName: "Código", width: 120 },
      { field: "activo", headerName: "Activo", width: 90 },
    ],
    marcamodelo: [
      { field: "id", headerName: "ID", width: 70 },
      { field: "marca", headerName: "Marca", flex: 1, minWidth: 150 },
      { field: "modelo", headerName: "Modelo", flex: 1, minWidth: 150 },
      {
        field: "precio",
        headerName: "Precio",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
      {
        field: "mprecio",
        headerName: "Mínimo",
        width: 120,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => fmtMoney(value),
      },
    ],
    sucursal: [
      { field: "sucursal", headerName: "Sucursal", width: 100 },
      { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 200 },
      { field: "direccion", headerName: "Dirección", flex: 1, minWidth: 200 },
      { field: "telefono1", headerName: "Teléfono", width: 140 },
    ],
  };

  const columns = [...columnasPorCatalogo[catalogo.clave], colAcciones];

  return (
    <Box
      component="main"
      sx={enHub ? undefined : { minHeight: "100vh", bgcolor: FP.color.surface }}
    >
      <PageHeader
        compact={enHub}
        title={soloCatalogo ? "Catálogo" : "Catálogos"}
        subtitle={
          soloCatalogo
            ? `${catalogo.etiqueta}: entradas canónicas con su precio de lista`
            : "Carteras, oficiales, marcas y modelos, y sucursales"
        }
        cta={
          <Stack direction="row" spacing={1}>
            {catalogo.clave === "marcamodelo" && (
              <>
                <input
                  ref={inputExcelRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  hidden
                  onChange={seleccionarExcel}
                />
                <Button
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() =>
                    downloadTemplateXlsx("catalogo_precios", [
                      "Marca",
                      "Modelo",
                      "Precio",
                      "Minimo",
                    ])
                  }
                  sx={btnGhost}
                >
                  Plantilla
                </Button>
                <Button
                  startIcon={<UploadFileOutlinedIcon />}
                  onClick={() => inputExcelRef.current?.click()}
                  sx={btnOutline(FP.color.navy)}
                >
                  Cargar Excel
                </Button>
              </>
            )}
            <Button startIcon={<AddIcon />} onClick={abrirNuevo} sx={btnOutline(FP.color.navy)}>
              Nuevo
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={enHub ? sectionBoxHub : sectionBox}>
          {catalogos.length > 1 && (
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}
            >
              {catalogos.map((c) => (
                <Tab key={c.clave} label={c.etiqueta} />
              ))}
            </Tabs>
          )}
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Typography
                sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: "0.85rem" }}
              >
                {rows.length} registros
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={columns}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Alta / edición */}
      <Dialog
        open={editando !== null}
        onClose={() => setEditando(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editando === 0 ? `Nueva ${catalogo.singular}` : `Editar ${catalogo.singular}`}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            {catalogo.clave === "tipodocumento" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={form.descripcion || ""}
                    onChange={setCampo("descripcion")}
                    helperText="Es el nombre que viaja en los documentos: sin '*'"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tarifa (RD$)"
                    value={form.precio ?? ""}
                    onChange={setCampo("precio")}
                    helperText="0 = sin tarifa"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Activo"
                    value={form.activo === "N" ? "N" : "S"}
                    onChange={setCampo("activo")}
                    sx={fpInputSx}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            {catalogo.clave === "cartera" && (
              <>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={form.descripcion || ""}
                    onChange={setCampo("descripcion")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    label="Activo"
                    value={form.activo === "N" ? "N" : "S"}
                    onChange={setCampo("activo")}
                    sx={fpInputSx}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            {catalogo.clave === "oficial" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={form.nombre || ""}
                    onChange={setCampo("nombre")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Código"
                    value={form.codigo || ""}
                    onChange={setCampo("codigo")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    fullWidth
                    label="Activo"
                    value={form.activo === "N" ? "N" : "S"}
                    onChange={setCampo("activo")}
                    sx={fpInputSx}
                  >
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </TextField>
                </Grid>
              </>
            )}
            {catalogo.clave === "marcamodelo" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Marca"
                    value={form.marca || ""}
                    onChange={setCampo("marca")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Modelo"
                    value={form.modelo || ""}
                    onChange={setCampo("modelo")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Precio"
                    placeholder="0.00"
                    value={form.precio || ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, precio: e.target.value.replace(/[^0-9.]/g, "") }))
                    }
                    helperText="Precio de lista en RD$; vacío = sin definir"
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mínimo"
                    placeholder="0.00"
                    value={form.mprecio || ""}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, mprecio: e.target.value.replace(/[^0-9.]/g, "") }))
                    }
                    helperText="Precio mínimo permitido en RD$"
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
            {catalogo.clave === "sucursal" && (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Sucursal"
                    value={form.sucursal || ""}
                    disabled={editando !== 0}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, sucursal: e.target.value.replace(/\D/g, "") }))
                    }
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <TextField
                    fullWidth
                    label="Descripción"
                    value={form.descripcion || ""}
                    onChange={setCampo("descripcion")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    label="Dirección"
                    value={form.direccion || ""}
                    onChange={setCampo("direccion")}
                    sx={fpInputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={form.telefono1 || ""}
                    onChange={setCampo("telefono1")}
                    sx={fpInputSx}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditando(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={guardar}
            disabled={guardando}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Carga masiva del catálogo de precios */}
      <Dialog
        open={carga !== null}
        onClose={() => setCarga(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Cargar catálogo de precios</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5 }}>
            <b>{carga?.fileName}</b>: {carga?.filas.length} filas — <b>{carga?.nuevas}</b> modelos
            nuevos y <b>{carga?.actualizadas}</b> existentes que se actualizarán con el dato del
            Excel. Las celdas de precio vacías no pisan lo que ya hay en el catálogo.
          </DialogContentText>
          {(carga?.filas || []).slice(0, 5).map((f, i) => (
            <Typography
              // eslint-disable-next-line react/no-array-index-key
              key={`${i}-${f.marca}-${f.modelo}`}
              sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}
            >
              {f.marca} {f.modelo} — precio {fmtMoney(f.precio)} / mínimo {fmtMoney(f.mprecio)}
            </Typography>
          ))}
          {(carga?.filas.length || 0) > 5 && (
            <Typography sx={{ fontSize: "0.85rem", color: FP.color.textMuted }}>
              … y {carga.filas.length - 5} filas más
            </Typography>
          )}
          {(carga?.errores || []).length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              {carga.errores.slice(0, 3).map((e) => (
                <Typography key={e} sx={{ fontSize: "0.85rem", color: "#B3261E" }}>
                  {e}
                </Typography>
              ))}
              {carga.errores.length > 3 && (
                <Typography sx={{ fontSize: "0.85rem", color: "#B3261E" }}>
                  … y {carga.errores.length - 3} filas más se omitirán
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setCarga(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              subiendo ? <CircularProgress size={16} color="inherit" /> : <UploadFileOutlinedIcon />
            }
            onClick={subirCarga}
            disabled={subiendo}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Cargar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aplicar precios del catálogo al inventario */}
      <Dialog
        open={aplicando !== null}
        onClose={() => setAplicando(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Aplicar precios al inventario</DialogTitle>
        <DialogContent>
          {unidades === null ? (
            <Stack alignItems="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : unidades === 0 ? (
            <DialogContentText>
              No hay unidades disponibles de{" "}
              <b>
                {aplicando ? `${aplicando.marca || ""} ${aplicando.modelo || ""}`.trim() : ""}
              </b>{" "}
              en el inventario.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Se actualizarán las <b>{unidades}</b> unidades disponibles de{" "}
              <b>
                {aplicando ? `${aplicando.marca || ""} ${aplicando.modelo || ""}`.trim() : ""}
              </b>{" "}
              con{" "}
              {parseFloat(aplicando?.precio) > 0 && (
                <>
                  precio <b>RD$ {fmtMoney(aplicando?.precio)}</b>
                </>
              )}
              {parseFloat(aplicando?.precio) > 0 && parseFloat(aplicando?.mprecio) > 0 && " y "}
              {parseFloat(aplicando?.mprecio) > 0 && (
                <>
                  mínimo <b>RD$ {fmtMoney(aplicando?.mprecio)}</b>
                </>
              )}
              . Las unidades vendidas, incautadas o en tránsito no se tocan.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setAplicando(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              aplicandoRun ? <CircularProgress size={16} color="inherit" /> : <SellOutlinedIcon />
            }
            onClick={aplicar}
            disabled={aplicandoRun || unidades === null || unidades === 0}
            sx={btnSolid(FP.color.navy, FP.color.navySoft)}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmar eliminación */}
      <Dialog
        open={eliminando !== null}
        onClose={() => setEliminando(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar registro</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Seguro que desea eliminar{" "}
            <b>
              {eliminando
                ? eliminando.descripcion ||
                  eliminando.nombre ||
                  `${eliminando.marca || ""} ${eliminando.modelo || ""}`.trim()
                : ""}
            </b>
            ? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEliminando(null)} sx={btnGhost}>
            Cancelar
          </Button>
          <Button
            startIcon={
              borrando ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />
            }
            onClick={eliminar}
            disabled={borrando}
            sx={btnSolid("#B3261E", "#8C1D18")}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
